import {gql} from '@apollo/client';
import React, {useState} from 'react';
import _ from 'underscore';

import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {
  FIELD_TYPES_BY_REGISTRATION_TABLE_TYPE,
  RegistrationTable,
  RegistrationTableConfig,
} from 'notebook/registration-tables/RegistrationTableConstants';
import {FieldType} from 'schemas/fields/SchemaFieldConstants';
import {ButtonStyle} from 'ui-library/buttons/Button';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import HorizontalThrobber from 'ui-library/HorizontalThrobber';
import AddIcon from 'ui-library/icons/AddIcon';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import Menu from 'ui-library/menus/Menu';
import Option from 'ui-library/menus/Option';
import Modal from 'ui-library/modals/Modal';
import {Cell, Column, Table} from 'ui-library/simple-table/SimpleTable';
import {INPUT_SAMPLES_SOURCE_NAME, INPUT_SAMPLES_SOURCE_UUID} from 'workflows/WorkflowConstants';

import {ManageSourcesModalQuery} from './__generated/ManageSourcesModalQuery';

function DeletableCell({children, onDelete}: {children: React.ReactNode; onDelete: () => void}): JSX.Element {
  return (
    <Cell className="modal-deletableCell">
      <div className="u-lr-split">
        {children}
        <a className="faded u-danger-pointer" onClick={onDelete}>
          <BenchlingIcon icon="delete" />
        </a>
      </div>
    </Cell>
  );
}

interface Props {
  // The input table is represented by a fake UUID.
  // This interface is not ideal and should ideally mirror DataTableConfig, with isWatchingInputTable
  // and watchedRegistrationProvenanceUuids.
  initialSourceTableUuids: Array<string>;
  onRequestClose: () => void;
  onRequestSubmit: (sourceTableUuids: Array<string>, newSourceTableUuids: Array<string>) => void;

  // When managing sources for a registration table, you can omit these two props
  registrationTables?: Array<RegistrationTableConfig | RegistrationTable>;
  linkType?: FieldType.BATCH_LINK | FieldType.BIOENTITY_LINK;
}

export const MANAGE_SOURCES_MODAL_QUERY = gql`
  query ManageSourcesModalQuery @skipAudit {
    ...AllTagSchemasGraphQL
  }
  ${getFragmentWrapper('AllTagSchemas').fragment}
`;

export default function ManageSourcesModal(props: Props): JSX.Element {
  // Filter out registration tables that are no longer in this entry
  const {registrationTables = []} = props;
  const initialSourceUuids = props.initialSourceTableUuids.filter(
    (uuid) => uuid === INPUT_SAMPLES_SOURCE_UUID || findSourceTable(uuid) != null
  );
  const [sourceTableUuids, setSourceTableUuids] = useState(initialSourceUuids);
  const [newSourceTableUuids, setNewSourceTableUuids] = useState([] as Array<string>);

  // TODO(BNCH-12833): Optimize this query! We don't need to retrieve the entire universe of tag schemas;
  //  we only need the ones associated with the registration tables we are given (filtered by type)
  const tagSchemasQueryResult = useBenchlingQuery<ManageSourcesModalQuery>(MANAGE_SOURCES_MODAL_QUERY);
  let areTagSchemasLoaded = false;
  if (tagSchemasQueryResult.type === QueryResultType.SUCCESS) {
    areTagSchemasLoaded = registrationTables.every(({tagSchemaId}) =>
      tagSchemasQueryResult.data.tagSchemas.find((tagSchema) => tagSchema.id === tagSchemaId)
    );
  }

  function findSourceTable(sourceTableUuid: string): RegistrationTableConfig {
    return _.findWhere(registrationTables, {
      provenanceUuid: sourceTableUuid,
    }) as RegistrationTableConfig;
  }

  function getSourceTableName(sourceTableUuid: string): string {
    return sourceTableUuid === INPUT_SAMPLES_SOURCE_UUID
      ? INPUT_SAMPLES_SOURCE_NAME
      : findSourceTable(sourceTableUuid).name;
  }

  function getAvailableSourceTableUuids(): Array<string> {
    const matchingRegistrationTables = registrationTables.filter(
      ({tableType}) => FIELD_TYPES_BY_REGISTRATION_TABLE_TYPE[tableType].type === props.linkType
    );
    const allSourceTableUuids = [
      INPUT_SAMPLES_SOURCE_UUID,
      ...matchingRegistrationTables.map(({provenanceUuid}) => provenanceUuid),
    ];
    return allSourceTableUuids.filter(
      (uuid) => !sourceTableUuids.includes(uuid) && !newSourceTableUuids.includes(uuid)
    );
  }

  function canAddNewSourceTable(): boolean {
    return getAvailableSourceTableUuids().length > 0;
  }

  function handleChangeSourceTable(index: number, sourceTableUuid: string): void {
    const updatedTableUuids = [...newSourceTableUuids];
    updatedTableUuids[index] = sourceTableUuid;
    setNewSourceTableUuids(updatedTableUuids);
  }

  function renderExistingSourceCell(index: number): JSX.Element {
    const sourceTableUuid = sourceTableUuids[index];
    return (
      <DeletableCell
        onDelete={() => {
          setSourceTableUuids(sourceTableUuids.filter((uuid) => uuid !== sourceTableUuid));
        }}
      >
        {getSourceTableName(sourceTableUuid)}
      </DeletableCell>
    );
  }

  function getNewSourceDropdownItems(index: number, selectedSourceTableUuid: string): React.ReactNode {
    return canAddNewSourceTable() ? (
      getAvailableSourceTableUuids().map((sourceTableUuid) => (
        <Option
          key={sourceTableUuid}
          value={sourceTableUuid}
          onSelect={() => {
            handleChangeSourceTable(index, sourceTableUuid);
          }}
        >
          {getSourceTableName(sourceTableUuid)}
        </Option>
      ))
    ) : (
      <Option
        key={selectedSourceTableUuid}
        value={selectedSourceTableUuid}
        onSelect={() => {
          handleChangeSourceTable(index, selectedSourceTableUuid);
        }}
      >
        {getSourceTableName(selectedSourceTableUuid)}
      </Option>
    );
  }

  function renderNewSourceCell(index: number): JSX.Element {
    const selectedSourceTableUuid = newSourceTableUuids[index];
    return (
      <DeletableCell
        onDelete={() => {
          setNewSourceTableUuids(newSourceTableUuids.filter((uuid) => uuid !== selectedSourceTableUuid));
        }}
      >
        <SelectDropdown
          menu={
            <Menu height={Menu.HEIGHTS.MEDIUM} width={Menu.WIDTHS.FULL} value={selectedSourceTableUuid}>
              {getNewSourceDropdownItems(index, selectedSourceTableUuid)}
            </Menu>
          }
          displayName={getSourceTableName(selectedSourceTableUuid)}
        />
      </DeletableCell>
    );
  }

  return (
    <Modal
      title="Manage sources"
      onRequestClose={props.onRequestClose}
      onRequestSubmit={() => {
        props.onRequestSubmit(sourceTableUuids, newSourceTableUuids);
      }}
      buttons={[
        {
          text: 'Save',
          submit: true,
          style: ButtonStyle.SECONDARY,
          disabled: !areTagSchemasLoaded,
          key: 'save',
        },
      ]}
    >
      {areTagSchemasLoaded ? (
        <Table
          emptyTableText="No input sources configured"
          numRows={sourceTableUuids.length + newSourceTableUuids.length}
        >
          <Column
            header={
              <Cell>
                Watched sources
                {canAddNewSourceTable() ? (
                  <AddIcon
                    onClick={() => {
                      setNewSourceTableUuids([...newSourceTableUuids, getAvailableSourceTableUuids()[0]]);
                    }}
                  />
                ) : null}
              </Cell>
            }
            renderCell={(rowIndex: number) =>
              rowIndex < sourceTableUuids.length
                ? renderExistingSourceCell(rowIndex)
                : renderNewSourceCell(rowIndex - sourceTableUuids.length)
            }
          />
        </Table>
      ) : (
        <div className="text-center">
          <HorizontalThrobber />
        </div>
      )}
    </Modal>
  );
}
